'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import useProducts from '@hooks/product/useProducts';
import { useCBQueue } from '@hooks/useCBQueue';
import { useVipAccess } from '@hooks/customer';
import { buildFilters, SCHEMA_FILTERS } from '@functions/category-grid/product-grid-functions';
import type { ProductGridProps } from '@typings/productGrid';
import { logProduct } from '@utils/productWrapper';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useIntersectionObserver } from '@repo/ui/src/hooks/useIntersectionObserver';
import GridItem from './_GridItem';
import { useProductGridData } from './useProductGridData';
import PaginatedGrid, { GridType } from '@components/PaginatedGrid';

interface LoadMoreProps {
  page: number;
}

const ProductGridClient = ({
  filters,
  filterOptions,
  sortOptions,
  _data,
  products: _products,
  hideDiscount,
  ...props
}: Omit<ProductGridProps, '_data'> & { _data?: ProductGridProps['_data'] }) => {
  const { locale } = useCurrentUrl();
  const gridRef = useRef<HTMLDivElement>(null);
  const hasFiredListViewed = useRef(false);

  const entry = useIntersectionObserver(gridRef, {
    threshold: 0.1,
    freezeOnceVisible: true,
  });

  const sort = useMemo(() => {
    if (sortOptions?.options?.[0]?.value) {
      const [field, direction] = sortOptions.options[0].value.split(',');
      return { field, direction };
    }
  }, [sortOptions]);

  const { data, setSize, isValidating, isLoading } = useProductGridData(
    {
      filters: buildFilters(filters, locale),
      _data,
      sort,
      options: {
        facets: filterOptions
          ?.map((x) => SCHEMA_FILTERS[x._meta?.schema as keyof typeof SCHEMA_FILTERS])
          ?.filter((x) => x),
      },
    },
    !!(_products?.length || _data),
  );

  const { vip, isLoading: isVipLoading } = useVipAccess();
  const { products } = useProducts(data?.hits);
  // Hide non-orderable items via CSS rather than filtering them
  // from the array. This keeps the items in the DOM for SEO/crawlers while
  // hiding them visually until VIP status resolves.
  const isItemHidden = React.useCallback(
    (item: any) => !!(!item.isOrderable && (isVipLoading || vip)),
    [vip, isVipLoading],
  );

  useEffect(() => {
    if (hasFiredListViewed.current || isVipLoading || isValidating || !products.length || !entry?.isIntersecting) {
      return;
    }

    hasFiredListViewed.current = true;
    logProduct('PRODUCT_LIST_VIEWED', {
      product: products,
      locale,
      list_name: props._meta.name,
      list_id: props._meta.deliveryId,
    });
  }, [entry?.isIntersecting, products, isVipLoading, isValidating, locale, props._meta.name, props._meta.deliveryId]);

  const getNextPageData = React.useCallback(
    async ({ page }: LoadMoreProps) => {
      const data = await setSize(page + 1);
      return { data: data?.[0] };
    },
    [setSize],
  );
  const loadData = useCBQueue(getNextPageData, !isLoading);

  return (
    <div ref={gridRef}>
      <PaginatedGrid
        items={products}
        nbPages={data.nbPages}
        page={data.page}
        loadData={loadData}
        type={GridType.Product}
        GridItemComponent={GridItem}
        hideDiscount={hideDiscount}
        isItemHidden={isItemHidden}
        {...props}
      />
    </div>
  );
};

export default ProductGridClient;
